import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function SharedTrip() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    fetch(`${API}/trip/shared/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Trip not found or link has expired');
        return res.json();
      })
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
    // Companion comments — anyone with the link can comment on picks
    fetch(`${API}/trip/shared/${token}/comments`)
      .then(r => r.ok ? r.json() : { comments: [] })
      .then(d => setComments(d.comments || []))
      .catch(() => {});
  }, [token]);

  const refreshComments = () => {
    fetch(`${API}/trip/shared/${token}/comments`)
      .then(r => r.json())
      .then(d => setComments(d.comments || []));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-bold text-teal mb-2">PointsPilot</div>
          <div className="text-brand-ink/65">Loading shared trip...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-navy text-white flex items-center justify-center">
        <div className="text-center max-w-sm">
          <div className="text-2xl font-bold text-teal mb-4">PointsPilot</div>
          <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-6">
            <div className="text-lg font-bold mb-2">Trip Not Found</div>
            <p className="text-brand-ink/70 text-sm mb-4">{error || 'This shared trip link may have expired or is invalid.'}</p>
            <Link to="/" className="px-4 py-2 bg-teal text-navy font-bold rounded-lg text-sm hover:bg-teal-dark transition">
              Go to PointsPilot
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { trip, destination, flight, hotel, activities, restaurants, budget } = data;

  const startFormatted = trip.date_range_start
    ? new Date(trip.date_range_start + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' })
    : null;
  const endFormatted = trip.date_range_end
    ? new Date(trip.date_range_end + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const nights = trip.date_range_start && trip.date_range_end
    ? Math.round((new Date(trip.date_range_end) - new Date(trip.date_range_start)) / (1000 * 60 * 60 * 24))
    : null;

  const totalPoints = (flight?.total_points_two_travelers || 0) + (hotel?.total_points || 0);
  const totalCash = (flight?.total_estimated_taxes || 0) +
    activities.reduce((s, a) => s + (a.price_for_two || 0), 0) +
    restaurants.reduce((s, r) => s + (r.avg_meal_for_two || 0), 0);

  return (
    <div className="min-h-screen bg-navy text-white">
      {/* Header */}
      <header className="bg-navy/95 backdrop-blur border-b border-brand-ink/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-teal">PointsPilot</Link>
          <span className="text-xs text-brand-ink/55 px-2 py-1 rounded bg-brand-ink/5">Shared Trip</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-20 space-y-6">
        {/* Hero */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-teal/20 via-navy-light to-navy-light border border-brand-ink/10">
          <div className="p-6 sm:p-8">
            <div className="text-xs text-teal font-bold uppercase tracking-wider mb-1">Shared Trip</div>
            <h1 className="text-4xl font-black">
              {destination ? destination.city : trip.name}
            </h1>
            {destination && <div className="text-brand-ink/80 mt-1">{destination.country}</div>}
            {destination?.vibe_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {destination.vibe_tags.map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-brand-ink/5 rounded-full text-xs text-brand-ink/70">{tag}</span>
                ))}
              </div>
            )}
            {startFormatted && endFormatted && (
              <div className="text-brand-ink/70 text-sm mt-3">
                {startFormatted} — {endFormatted}
                {nights && <span> &middot; {nights} nights</span>}
              </div>
            )}
            <div className="text-brand-ink/65 text-xs mt-2">
              {trip.travelers} travelers &middot; {trip.cabin_class} class
            </div>
          </div>
        </div>

        {/* Destination description */}
        {destination?.description && (
          <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-5">
            <p className="text-brand-ink/75 text-sm leading-relaxed">{destination.description}</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Points Used" value={totalPoints.toLocaleString()} sub="Amex + Chase" color="text-teal" />
          <StatCard label="Cash Cost" value={`$${totalCash.toLocaleString()}`} sub="Taxes + extras" color="text-gold" />
          <StatCard label="Points Left" value={((budget?.amex_remaining || 0) + (budget?.chase_remaining || 0)).toLocaleString()} sub="Still available" color="text-brand-ink/80" />
          <StatCard
            label="Items Planned"
            value={(flight ? 1 : 0) + (hotel ? 1 : 0) + activities.length + restaurants.length}
            sub="Flights, hotel, etc."
            color="text-teal"
          />
        </div>

        {/* Flight */}
        {flight && (
          <Section title="Flights" icon="✈️">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{flight.routing_description}</div>
                <div className="text-sm text-brand-ink/70 mt-1">
                  via {flight.airline_program} &middot; {flight.flight_duration_outbound}
                  {flight.is_nonstop ? ' · Nonstop' : flight.num_stops ? ` · ${flight.num_stops} stop` : ''}
                </div>
                {flight.aircraft_type && (
                  <div className="text-xs text-brand-ink/55 mt-1">{flight.aircraft_type} &middot; {flight.seat_product}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-teal">{(flight.total_points_two_travelers || 0).toLocaleString()} pts</div>
                {flight.total_estimated_taxes > 0 && <div className="text-xs text-brand-ink/65">+ ${flight.total_estimated_taxes} taxes</div>}
              </div>
            </div>
          </Section>
        )}

        {/* Hotel */}
        {hotel && (
          <Section title="Hotel" icon="🏨">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{hotel.hotel_name}</div>
                <div className="text-sm text-brand-ink/70 mt-1">
                  {hotel.nights} nights via {hotel.hotel_program}
                  {hotel.neighborhood && ` · ${hotel.neighborhood}`}
                </div>
                {hotel.amenities?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {hotel.amenities.slice(0, 5).map(a => (
                      <span key={a} className="px-2 py-0.5 bg-brand-ink/5 rounded text-[10px] text-brand-ink/65">{a}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-lg font-black text-teal">{(hotel.total_points || 0).toLocaleString()} pts</div>
                <div className="text-xs text-brand-ink/65">{(hotel.points_per_night || 0).toLocaleString()}/night</div>
              </div>
            </div>
          </Section>
        )}

        {/* Activities */}
        {activities.length > 0 && (
          <Section title="Activities" icon="🎯">
            <div className="space-y-3">
              {activities.map((a, i) => (
                <div key={i} className="flex items-start justify-between py-2 border-b border-brand-ink/5 last:border-0">
                  <div>
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-brand-ink/65 mt-0.5">
                      {a.category}{a.duration && ` · ${a.duration}`}
                    </div>
                    {a.description && <div className="text-xs text-brand-ink/55 mt-1 max-w-md">{a.description}</div>}
                  </div>
                  {a.price_for_two > 0 && (
                    <div className="text-sm text-gold font-medium whitespace-nowrap ml-4">${a.price_for_two}</div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Restaurants */}
        {restaurants.length > 0 && (
          <Section title="Restaurants" icon="🍽️">
            <div className="space-y-3">
              {restaurants.map((r, i) => (
                <div key={i} className="flex items-start justify-between py-2 border-b border-brand-ink/5 last:border-0">
                  <div>
                    <div className="font-medium text-sm">{r.name}</div>
                    <div className="text-xs text-brand-ink/65 mt-0.5">
                      {r.cuisine}{r.price_range && ` · ${r.price_range}`}
                    </div>
                    {r.description && <div className="text-xs text-brand-ink/55 mt-1 max-w-md">{r.description}</div>}
                  </div>
                  {r.avg_meal_for_two > 0 && (
                    <div className="text-sm text-gold font-medium whitespace-nowrap ml-4">${r.avg_meal_for_two}</div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Budget Summary */}
        {budget && (
          <Section title="Budget Summary" icon="💰">
            <div className="space-y-2">
              <BudgetRow label="Amex MR Used" value={`${(budget.amex_used || 0).toLocaleString()} pts`} color="text-blue-300" />
              <BudgetRow label="Chase UR Used" value={`${(budget.chase_used || 0).toLocaleString()} pts`} color="text-indigo-300" />
              <BudgetRow label="Cash Costs" value={`$${(budget.cash_total || 0).toLocaleString()}`} color="text-gold" />
              <div className="border-t border-brand-ink/10 pt-2 mt-3">
                <BudgetRow label="Total Points Used" value={`${totalPoints.toLocaleString()}`} color="text-teal" bold />
                <BudgetRow label="Amex MR Remaining" value={`${(budget.amex_remaining || 0).toLocaleString()}`} color="text-brand-ink/70" />
                <BudgetRow label="Chase UR Remaining" value={`${(budget.chase_remaining || 0).toLocaleString()}`} color="text-brand-ink/70" />
              </div>
            </div>
          </Section>
        )}

        {/* Footer */}
        {/* Companion comments — anyone with the link can leave feedback */}
        <CompanionComments token={token} comments={comments} onPosted={refreshComments} />

        <div className="text-center py-8 border-t border-brand-ink/10">
          <div className="text-sm text-brand-ink/55">Shared via PointsPilot</div>
          <div className="text-xs text-brand-ink/40 mt-1">Travel planning powered by points</div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-3 text-center">
      <div className={`text-xl font-black ${color}`}>{value}</div>
      <div className="text-[10px] text-brand-ink/65 uppercase font-bold mt-0.5">{label}</div>
      <div className="text-[10px] text-brand-ink/45">{sub}</div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-navy-light rounded-xl border border-brand-ink/10 p-5">
      <div className="flex items-center gap-2 mb-3">
        <span>{icon}</span>
        <h3 className="font-bold text-white">{title}</h3>
      </div>
      {children}
    </div>
  );
}

// Companion comments — anyone with the share link can leave a comment.
// Owner sees them when they reopen the link. Travel partner can react
// to the plan ("love this hotel" / "can we swap the Tuesday dinner?")
// without needing app access.
function CompanionComments({ token, comments, onPosted }) {
  const [name, setName] = useState(() => localStorage.getItem('pp_companion_name') || '');
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      localStorage.setItem('pp_companion_name', name);
      await fetch(`${API}/trip/shared/${token}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pick_kind: 'trip', pick_key: 'general', commenter_name: name || 'Anonymous', comment_text: text.trim() }),
      });
      setText('');
      onPosted();
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-navy-light rounded-xl border border-teal/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span>💬</span>
        <h3 className="font-bold text-white">Comments + Notes</h3>
        <span className="text-[10px] text-brand-ink/55 ml-auto">{comments.length}</span>
      </div>
      <p className="text-[11px] text-brand-ink/65 mb-3">Travel partner? Leave a note — the trip owner will see it next time they open the plan.</p>

      {comments.length > 0 && (
        <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
          {comments.map(c => (
            <div key={c.id} className="bg-navy rounded-lg p-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-bold text-teal">{c.commenter_name || 'Anonymous'}</span>
                <span className="text-[10px] text-brand-ink/55">{new Date(c.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              </div>
              <p className="text-xs text-brand-ink/90 mt-1 leading-snug whitespace-pre-wrap">{c.comment_text}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        <input type="text" placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-1.5 text-xs text-white placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none" />
        <textarea placeholder="Add a comment about the plan…" value={text} onChange={(e) => setText(e.target.value)} rows={2}
          className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-1.5 text-xs text-white placeholder-brand-ink/45 focus:border-teal/50 focus:outline-none resize-none" />
        <button onClick={post} disabled={!text.trim() || posting} className="w-full bg-teal text-navy font-bold text-xs py-2 rounded-lg disabled:opacity-40">
          {posting ? 'Posting…' : 'Post comment'}
        </button>
      </div>
    </div>
  );
}

function BudgetRow({ label, value, color, bold }) {
  return (
    <div className={`flex justify-between py-0.5 text-sm ${bold ? 'font-bold' : ''}`}>
      <span className="text-brand-ink/75">{label}</span>
      <span className={color}>{value}</span>
    </div>
  );
}
